import React from 'react';
import { MapPin, Phone, Mail, Instagram, Facebook, MessageCircle } from 'lucide-react';

// Contact information - easy to update
const CONTACT_INFO = {
  address: "R. Portugal, 161 - Bom Jardim, São José do Rio Preto - SP, 15084-070",
  phone: "+55 11 99557-9395",
  email: "lucaspessoa.adv@gmail.com",
};

// Social media links - easy to update
const SOCIAL_LINKS = {
  instagram: "https://instagram.com/pessoapessoa.adv",
  facebook: "https://facebook.com/pessoapessoaadvocacia",
  linkedin: "https://linkedin.com/company/pessoapessoaadvocacia",
};

// Navigation links
const NAV_LINKS = [
  { label: "Início", href: "#inicio" },
  { label: "Equipe", href: "#equipe" },
  { label: "Conteúdos", href: "#conteudos" },
  { label: "Quem somos", href: "#quem-somos" },
  { label: "Contato", href: "#contato" },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (href === "#inicio") {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer 
      className="relative w-full bg-navy-dark pt-16 lg:pt-20 overflow-hidden"
      data-testid="footer"
    >
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 pb-12 lg:pb-16">
          
          {/* Column 1 - Identity */}
          <div className="lg:col-span-1" data-testid="footer-identity">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                <span className="font-display text-xl text-accent font-bold">P</span>
              </div>
              <div>
                <h3 className="font-display text-xl text-primary-foreground font-semibold leading-tight">
                  Pessoa & Pessoa
                </h3>
                <span className="text-accent text-xs font-body tracking-wider uppercase">Advocacia</span>
              </div>
            </div>
            
            {/* Institutional text */}
            <p className="text-primary-foreground/60 font-body text-sm leading-relaxed">
              Defendendo seus direitos com ética, dedicação e excelência há mais de 15 anos.
            </p>
          </div>

          {/* Column 2 - Navigation */}
          <div data-testid="footer-navigation">
            <h4 className="font-display text-lg text-primary-foreground font-semibold mb-5">
              Navegação
            </h4>
            <nav>
              <ul className="space-y-3">
                {NAV_LINKS.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      onClick={(e) => scrollToSection(e, link.href)}
                      className="text-primary-foreground/60 font-body text-sm hover:text-accent transition-colors duration-300 inline-block"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Column 3 - Contact */}
          <div data-testid="footer-contact">
            <h4 className="font-display text-lg text-primary-foreground font-semibold mb-5">
              Contato
            </h4>
            <ul className="space-y-4">
              {/* Address */}
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <span className="text-primary-foreground/60 font-body text-sm leading-relaxed">
                  {CONTACT_INFO.address}
                </span>
              </li>
              
              {/* Phone */}
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-accent flex-shrink-0" />
                <a 
                  href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`}
                  className="text-primary-foreground/60 font-body text-sm hover:text-accent transition-colors duration-300"
                >
                  {CONTACT_INFO.phone}
                </a>
              </li>
              
              {/* Email */}
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-accent flex-shrink-0" />
                <a 
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-primary-foreground/60 font-body text-sm hover:text-accent transition-colors duration-300"
                >
                  {CONTACT_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4 - Social Media */}
          <div data-testid="footer-social">
            <h4 className="font-display text-lg text-primary-foreground font-semibold mb-5">
              Redes Sociais
            </h4>
            <p className="text-primary-foreground/60 font-body text-sm mb-5">
              Acompanhe nosso conteúdo e novidades jurídicas.
            </p>
            <div className="flex items-center gap-4">
              {/* Instagram */}
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center text-primary-foreground/60 hover:bg-accent/20 hover:text-accent transition-all duration-300"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              
              {/* Facebook */}
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center text-primary-foreground/60 hover:bg-accent/20 hover:text-accent transition-all duration-300"
                aria-label="Facebook"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              
              {/* LinkedIn */}
              <a
                href={`https://wa.me/5511995579395?text=Olá! Encontrei o escritório pelas redes sociais e gostaria de falar com um advogado.`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary-foreground/10 flex items-center justify-center text-primary-foreground/60 hover:bg-accent/20 hover:text-accent transition-all duration-300"
                aria-label="WhatsApp"
                data-testid="social-whatsapp"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-primary-foreground/20 to-transparent" />

        {/* Bottom Bar */}
        <div className="py-6 lg:py-8" data-testid="footer-bottom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 text-center lg:text-left">
            {/* Copyright */}
            <p className="text-primary-foreground/50 font-body text-sm">
              © {currentYear} Pessoa & Pessoa Advocacia. Todos os direitos reservados.
            </p>
            
            {/* Additional text */}
            <p className="text-primary-foreground/40 font-body text-xs">
              Desenvolvido com foco em ética, transparência e excelência jurídica.
            </p>
          </div>
        </div>
      </div>
      
      {/* Decorative Background Elements */}
      <div className="absolute bottom-0 left-0 w-full h-32 opacity-5 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full bg-accent blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-48 h-48 rounded-full bg-primary-foreground blur-3xl" />
      </div>
    </footer>
  );
};

export default Footer;
